/**
 * Utility functions for managing redirect URLs during authentication flows
 * 
 * Stores redirect destinations in both:
 * - URL query params (for passing through auth flows)
 * - sessionStorage (as fallback for cross-tab scenarios)
 */

const REDIRECT_STORAGE_KEY = "auth_redirect_to";
const BLOCKED_POST_AUTH_PATHS = new Set([
  "/",
  "/auth",
  "/auth/login",
  "/auth/signup",
  "/auth/forgot-password",
]);

function normalizePostAuthRedirect(url: string | null | undefined, fallback: string): string {
  if (!url || typeof url !== "string") return fallback;

  const trimmed = url.trim();
  if (!trimmed) return fallback;

  // Only allow same-origin absolute paths.
  if (!trimmed.startsWith("/") || trimmed.startsWith("//")) {
    return fallback;
  }

  try {
    const parsed = new URL(trimmed, "https://growthsync.local");
    const pathname = parsed.pathname || "/";

    // Never redirect an authenticated user back to logged-out/auth pages.
    if (BLOCKED_POST_AUTH_PATHS.has(pathname) || pathname.startsWith("/auth/")) {
      return fallback;
    }

    return `${pathname}${parsed.search}${parsed.hash}`;
  } catch {
    return fallback;
  }
}

/**
 * Store a redirect URL for use after authentication
 * @param url - The URL to redirect to after auth (e.g., "/tickets/123")
 */
export function storeRedirectUrl(url: string): void {
  if (typeof window === "undefined") return;
  
  try {
    const safeUrl = normalizePostAuthRedirect(url, "/dashboard");
    // Store in sessionStorage as fallback
    sessionStorage.setItem(REDIRECT_STORAGE_KEY, safeUrl);
  } catch (error) {
    console.warn("[Redirect Storage] Failed to store redirect URL:", error);
  }
}

/**
 * Get the stored redirect URL
 * @param fallback - Default URL if no redirect is stored (default: "/dashboard")
 * @returns The redirect URL or fallback
 */
export function getRedirectUrl(fallback: string = "/dashboard"): string {
  if (typeof window === "undefined") return fallback;
  
  try {
    const stored = sessionStorage.getItem(REDIRECT_STORAGE_KEY);
    if (stored) {
      return normalizePostAuthRedirect(stored, fallback);
    }
  } catch (error) {
    console.warn("[Redirect Storage] Failed to read redirect URL:", error);
  }
  
  return fallback;
}

/**
 * Clear the stored redirect URL
 */
export function clearRedirectUrl(): void {
  if (typeof window === "undefined") return;
  
  try {
    sessionStorage.removeItem(REDIRECT_STORAGE_KEY);
  } catch (error) {
    console.warn("[Redirect Storage] Failed to clear redirect URL:", error);
  }
}

/**
 * Get redirect URL from URL search params or sessionStorage
 * @param searchParams - URLSearchParams object from the current URL
 * @param fallback - Default URL if no redirect is found (default: "/dashboard")
 * @returns The redirect URL or fallback
 */
export function getRedirectFromParamsOrStorage(
  searchParams: URLSearchParams | null,
  fallback: string = "/dashboard"
): string {
  // First, check URL params (highest priority - from current auth flow)
  if (searchParams) {
    const redirectTo = searchParams.get("redirectTo");
    if (redirectTo) {
      const safeRedirect = normalizePostAuthRedirect(redirectTo, fallback);
      // Also store it for consistency
      storeRedirectUrl(safeRedirect);
      return safeRedirect;
    }
  }
  
  // Fallback to sessionStorage
  return getRedirectUrl(fallback);
}

/**
 * Build a login URL with redirect parameter
 * @param redirectTo - The URL to redirect to after login
 * @returns Login URL with redirectTo query param
 */
export function buildLoginUrl(redirectTo: string): string {
  // Store in sessionStorage as well
  storeRedirectUrl(redirectTo);
  
  // Build URL with query param
  const encoded = encodeURIComponent(redirectTo);
  return `/auth/login?redirectTo=${encoded}`;
}

/**
 * Build a signup URL with redirect parameter
 * @param redirectTo - The URL to redirect to after signup
 * @returns Signup URL with redirectTo query param
 */
export function buildSignupUrl(redirectTo: string): string {
  // Store in sessionStorage as well
  storeRedirectUrl(redirectTo);
  
  // Build URL with query param
  const encoded = encodeURIComponent(redirectTo);
  return `/auth/signup?redirectTo=${encoded}`;
}
